import type { PlannerLayout } from "@/components/planner/controllers/planner";
import { isRecord } from "@/utils/unknown";

export type CanvasParameterType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "datetime"
  | "id"
  | "json";

export type CanvasJsonValue =
  | null
  | boolean
  | number
  | string
  | CanvasJsonValue[]
  | { [key: string]: CanvasJsonValue };

export type CanvasParameter = {
  name: string;
  type: CanvasParameterType;
  required?: boolean;
  description?: string;
  default?: CanvasJsonValue;
};

/** Content widget: a database view. */
export type CanvasViewWidget = {
  kind: "view";
  /** Query string; this is what `Query.vue` persists as the view's source. */
  query: string;
  /**
   * Serialized display document of the view (layout plus every per-layout
   * display preference), emitted by `View.vue`'s display-controlled mode
   * (`parseViewDisplayConfig` in `@/components/views/display`). Opaque to
   * the canvas — its schema is owned by the views module — so it is only
   * validated as plain JSON here. Absent until the view emits its first
   * document (a fresh widget renders with the view's defaults).
   */
  display?: CanvasJsonValue;
};

/** Content widget: the agenda timeline. */
export type CanvasTimelineWidget = {
  kind: "timeline";
};

/**
 * The task sections a Tasks widget can display, in canonical display
 * order — the same groupings as the Agenda's task pane. "day" follows the
 * canvas's selected date (like the timeline widget), "week" is that date's
 * week.
 */
export const CANVAS_TASKS_SECTIONS = [
  "day",
  "week",
  "overdue",
  "upcoming",
  "unplanned",
  "completed",
  "discarded",
] as const;

export type CanvasTasksSection = (typeof CANVAS_TASKS_SECTIONS)[number];

/**
 * Content widget: the agenda task lists. `sections` are the enabled
 * groupings in canonical order; with several enabled each renders as a
 * retractable titled section, a single one renders as a bare list.
 */
export type CanvasTasksWidget = {
  kind: "tasks";
  sections: CanvasTasksSection[];
};

/**
 * The layouts a Calendar widget can default to: the planner's own layout
 * names, mirrored here (with a `satisfies` guard) so the canvas model stays
 * a pure data module with no runtime dependency on the planner.
 */
export const CANVAS_CALENDAR_LAYOUTS = [
  "1-day",
  "2-day",
  "3-day",
  "4-day",
  "5-day",
  "6-day",
  "week",
  "month",
] as const satisfies readonly PlannerLayout[];

export type CanvasCalendarLayout = (typeof CANVAS_CALENDAR_LAYOUTS)[number];

/**
 * Content widget: the planner calendar. Every toggle defaults to shown when
 * absent; `layout` is the widget's default planner layout ("week" when
 * absent) — viewers can still switch layouts when `layouts` is shown, and
 * that choice persists per widget outside the canvas body.
 */
export type CanvasCalendarWidget = {
  kind: "calendar";
  /** Default planner layout; "week" when absent. */
  layout?: CanvasCalendarLayout;
  /** Show the multi-timezone axis (default true). */
  timezones?: boolean;
  /** Show the zoom in/out buttons (default true). */
  zoom?: boolean;
  /** Show the layout selector buttons (default true). */
  layouts?: boolean;
  /** Show the per-day task footer (default true). */
  footer?: boolean;
};

/**
 * Layout widget: a grid of `rows` × `columns` slots ("1×2" = 1 row,
 * 2 columns). Each slot can hold any widget — including another layout
 * widget, so layouts nest without a depth limit.
 */
export type CanvasLayoutWidget = {
  kind: "layout";
  /** Number of rows, ≥ 1. */
  rows: number;
  /** Number of columns, ≥ 1. */
  columns: number;
  /** Row-major, dense, length `rows × columns`; `null` = empty slot. */
  slots: CanvasSlot[];
  /**
   * Optional relative row heights (like CSS `fr` units), length `rows`.
   * Rows share the height equally when absent.
   */
  rowSizes?: number[];
  /**
   * Optional relative column widths (like CSS `fr` units), length `columns`.
   * Columns share the width equally when absent.
   */
  columnSizes?: number[];
};

/**
 * Content widget: what a slot ultimately renders. Every supported widget
 * type is listed here — adding one means adding its variant to this union.
 */
export type CanvasContentWidget =
  | CanvasViewWidget
  | CanvasTimelineWidget
  | CanvasTasksWidget
  | CanvasCalendarWidget;

/**
 * A widget is either a layout widget (a grid of nested slots) or a content
 * widget (a leaf).
 */
export type CanvasWidgetBody = CanvasLayoutWidget | CanvasContentWidget;

export type CanvasWidget = {
  /**
   * Stable identity: per-widget state (e.g. view column widths) is keyed by
   * this id, so moving a widget to another slot preserves it.
   */
  id: string;
  widget: CanvasWidgetBody;
};

/**
 * A slot holds at most one widget; `null` keeps the slot empty (usable as
 * spacing). The whole canvas is slots all the way down: each tab is a single
 * root slot, and layout widgets are grids of child slots.
 */
export type CanvasSlot = CanvasWidget | null;

/**
 * How a tab occupies the vertical axis:
 * - `"screen"` (the default): the tab owns exactly the viewport below the
 *   canvas header — the page does not scroll, layout `rowSizes` divide the
 *   fixed height, and widgets scroll internally.
 * - `"page"`: the tab flows like a document — rows grow with their content
 *   and the whole page scrolls.
 */
export type CanvasTabHeight = "screen" | "page";

export type CanvasTab = {
  id: string;
  name: string;
  /** The tab's single root slot. */
  root: CanvasSlot;
  /** Vertical behavior; `"screen"` when absent. */
  height?: CanvasTabHeight;
};

/**
 * Canvas-level display options (the editor's "Canvas" section). Every
 * option is a toggle with a default, so an absent record means defaults.
 */
export type CanvasOptions = {
  /** Show the canvas title header; shown (`true`) when absent. */
  title?: boolean;
  /** Show the date picker in the top bar; hidden (`false`) when absent. */
  datepicker?: boolean;
};

export type CanvasBodyV1 = {
  schemaVersion: 1;
  tabs: CanvasTab[];
  inputParams: CanvasParameter[];
  outputParams: CanvasParameter[];
  options?: CanvasOptions;
};

/**
 * Path from a tab's root slot to a nested slot: the child slot index inside
 * each successive layout widget. The empty path is the root slot itself.
 */
export type CanvasSlotPath = number[];

const CANVAS_PARAMETER_TYPES: readonly CanvasParameterType[] = [
  "string",
  "number",
  "boolean",
  "date",
  "datetime",
  "id",
  "json",
];

function isCanvasParameterType(value: unknown): value is CanvasParameterType {
  return CANVAS_PARAMETER_TYPES.some((candidate) => candidate === value);
}

function isDenseArrayOf<T>(
  value: unknown,
  isEntry: (entry: unknown) => entry is T
): value is T[] {
  if (!Array.isArray(value)) return false;
  for (let index = 0; index < value.length; index += 1) {
    const descriptor = Object.getOwnPropertyDescriptor(value, index);
    if (!descriptor || !("value" in descriptor) || !isEntry(descriptor.value)) {
      return false;
    }
  }
  return true;
}

function isUnknown(_value: unknown): _value is unknown {
  return true;
}

function isCanvasJsonObject(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

type TraversalEntry = {
  value: unknown;
  leaving: boolean;
};

/**
 * Plain-JSON check (iterative, cycle-rejecting): used for parameter defaults
 * and the opaque blobs the canvas stores on behalf of widgets (e.g. a view
 * widget's display document).
 */
export function isCanvasJsonValue(value: unknown): value is CanvasJsonValue {
  const pending: TraversalEntry[] = [{ value, leaving: false }];
  const ancestors = new WeakSet<object>();

  while (pending.length > 0) {
    const entry = pending.pop();
    if (!entry) continue;
    const current = entry.value;

    if (entry.leaving) {
      if (typeof current === "object" && current !== null) {
        ancestors.delete(current);
      }
      continue;
    }
    if (
      current === null ||
      typeof current === "boolean" ||
      typeof current === "number" ||
      typeof current === "string"
    ) {
      continue;
    }
    if (!Array.isArray(current) && !isCanvasJsonObject(current)) return false;
    if (ancestors.has(current)) return false;

    ancestors.add(current);
    pending.push({ value: current, leaving: true });
    if (Array.isArray(current)) {
      for (let index = current.length - 1; index >= 0; index -= 1) {
        const descriptor = Object.getOwnPropertyDescriptor(current, index);
        if (!descriptor || !("value" in descriptor)) return false;
        pending.push({ value: descriptor.value, leaving: false });
      }
    } else {
      for (const key of Object.getOwnPropertyNames(current)) {
        const descriptor = Object.getOwnPropertyDescriptor(current, key);
        if (!descriptor || !("value" in descriptor)) return false;
        pending.push({ value: descriptor.value, leaving: false });
      }
    }
  }
  return true;
}

function isCanvasParameter(value: unknown): value is CanvasParameter {
  return (
    isRecord(value) &&
    typeof value.name === "string" &&
    isCanvasParameterType(value.type) &&
    (value.required === undefined || typeof value.required === "boolean") &&
    (value.description === undefined ||
      typeof value.description === "string") &&
    (value.default === undefined || isCanvasJsonValue(value.default))
  );
}

function isCanvasViewWidget(value: unknown): value is CanvasViewWidget {
  return (
    isRecord(value) &&
    value.kind === "view" &&
    typeof value.query === "string" &&
    (value.display === undefined || isCanvasJsonValue(value.display))
  );
}

function isCanvasTimelineWidget(value: unknown): value is CanvasTimelineWidget {
  return isRecord(value) && value.kind === "timeline";
}

function isCanvasTasksSection(value: unknown): value is CanvasTasksSection {
  return CANVAS_TASKS_SECTIONS.some((section) => section === value);
}

function isCanvasTasksWidget(value: unknown): value is CanvasTasksWidget {
  return (
    isRecord(value) &&
    value.kind === "tasks" &&
    isDenseArrayOf(value.sections, isCanvasTasksSection)
  );
}

function isCanvasCalendarLayout(value: unknown): value is CanvasCalendarLayout {
  return CANVAS_CALENDAR_LAYOUTS.some((layout) => layout === value);
}

function isCanvasCalendarWidget(value: unknown): value is CanvasCalendarWidget {
  const isOptionalBoolean = (entry: unknown) =>
    entry === undefined || typeof entry === "boolean";
  return (
    isRecord(value) &&
    value.kind === "calendar" &&
    (value.layout === undefined || isCanvasCalendarLayout(value.layout)) &&
    isOptionalBoolean(value.timezones) &&
    isOptionalBoolean(value.zoom) &&
    isOptionalBoolean(value.layouts) &&
    isOptionalBoolean(value.footer)
  );
}

/** One arm per supported content widget type (see `CanvasContentWidget`). */
function isCanvasContentWidget(value: unknown): value is CanvasContentWidget {
  return (
    isCanvasViewWidget(value) ||
    isCanvasTimelineWidget(value) ||
    isCanvasTasksWidget(value) ||
    isCanvasCalendarWidget(value)
  );
}

function isSlotCount(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1;
}

function isTrackSizes(value: unknown, count: number): boolean {
  if (value === undefined) return true;
  return (
    isDenseArrayOf(
      value,
      (entry): entry is number =>
        typeof entry === "number" && Number.isFinite(entry) && entry > 0
    ) && value.length === count
  );
}

/**
 * Validate a slot, traversing into layout widgets iteratively so arbitrarily
 * deep nesting cannot overflow the stack. Cyclic trees (a layout referencing
 * an ancestor slot) are rejected; shared non-cyclic subtrees are accepted.
 */
function isCanvasSlot(value: unknown): value is CanvasSlot {
  const pending: TraversalEntry[] = [{ value, leaving: false }];
  const ancestors = new WeakSet<object>();

  while (pending.length > 0) {
    const entry = pending.pop();
    if (!entry) continue;
    const current = entry.value;

    if (entry.leaving) {
      if (typeof current === "object" && current !== null) {
        ancestors.delete(current);
      }
      continue;
    }

    if (current === null) continue;
    if (!isRecord(current) || typeof current.id !== "string") return false;
    const body = current.widget;
    if (!isRecord(body)) return false;
    // Anything that is not a layout widget must be a content widget.
    if (body.kind !== "layout") {
      if (!isCanvasContentWidget(body)) return false;
      continue;
    }
    if (!isSlotCount(body.rows) || !isSlotCount(body.columns)) return false;
    if (
      !isTrackSizes(body.rowSizes, body.rows) ||
      !isTrackSizes(body.columnSizes, body.columns)
    ) {
      return false;
    }
    const slots = body.slots;
    if (!isDenseArrayOf(slots, isUnknown)) return false;
    if (slots.length !== body.rows * body.columns) return false;
    if (ancestors.has(current)) return false;

    ancestors.add(current);
    pending.push({ value: current, leaving: true });
    for (const slot of slots) {
      pending.push({ value: slot, leaving: false });
    }
  }
  return true;
}

function isCanvasTab(value: unknown): value is CanvasTab {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    (value.height === undefined ||
      value.height === "screen" ||
      value.height === "page") &&
    isCanvasSlot(value.root)
  );
}

function isCanvasOptions(value: unknown): value is CanvasOptions {
  return (
    isRecord(value) &&
    (value.title === undefined || typeof value.title === "boolean") &&
    (value.datepicker === undefined || typeof value.datepicker === "boolean")
  );
}

function isCanvasBodyV1(value: unknown): value is CanvasBodyV1 {
  return (
    isRecord(value) &&
    value.schemaVersion === 1 &&
    isDenseArrayOf(value.tabs, isCanvasTab) &&
    isDenseArrayOf(value.inputParams, isCanvasParameter) &&
    isDenseArrayOf(value.outputParams, isCanvasParameter) &&
    (value.options === undefined || isCanvasOptions(value.options))
  );
}

/**
 * Validate a persisted canvas body without discarding unknown future keys.
 * Every property required by the current CanvasBodyV1 contract is checked.
 */
export function parseCanvasBodyV1(value: unknown): CanvasBodyV1 | null {
  return isCanvasBodyV1(value) ? value : null;
}

/*
 * Slot-tree helpers. All updates are immutable: they return a new tree
 * (sharing unchanged subtrees) so the editor's dirty tracking and undo paths
 * can compare snapshots. Depth is only bounded by the document, but these run
 * on documents the editor itself built, whose nesting is shallow.
 */

/** The slot at `path`, or `undefined` when the path does not exist. */
export function getSlotAt(
  root: CanvasSlot,
  path: CanvasSlotPath
): CanvasSlot | undefined {
  let slot = root;
  for (const index of path) {
    if (!slot || slot.widget.kind !== "layout") return undefined;
    const child = slot.widget.slots[index];
    if (child === undefined) return undefined;
    slot = child;
  }
  return slot;
}

/** Replace the slot at `path`; no-op when the path does not exist. */
export function setSlotAt(
  root: CanvasSlot,
  path: CanvasSlotPath,
  value: CanvasSlot
): CanvasSlot {
  if (path.length === 0) return value;
  if (!root || root.widget.kind !== "layout") return root;
  const [index, ...rest] = path;
  if (index === undefined) return root;
  const child = root.widget.slots[index];
  if (child === undefined) return root;
  const nextChild = setSlotAt(child, rest, value);
  if (nextChild === child) return root;
  const slots = root.widget.slots.slice();
  slots[index] = nextChild;
  return { ...root, widget: { ...root.widget, slots } };
}

/** Apply `update` to the widget with the given id, wherever it is. */
export function updateWidgetById(
  root: CanvasSlot,
  id: string,
  update: (widget: CanvasWidget) => CanvasWidget
): CanvasSlot {
  if (!root) return root;
  if (root.id === id) return update(root);
  if (root.widget.kind !== "layout") return root;
  let changed = false;
  const slots = root.widget.slots.map((slot) => {
    const next = updateWidgetById(slot, id, update);
    if (next !== slot) changed = true;
    return next;
  });
  if (!changed) return root;
  return { ...root, widget: { ...root.widget, slots } };
}

/** Empty the slot holding the widget with the given id (subtree included). */
export function removeWidgetById(root: CanvasSlot, id: string): CanvasSlot {
  if (!root) return root;
  if (root.id === id) return null;
  if (root.widget.kind !== "layout") return root;
  let changed = false;
  const slots = root.widget.slots.map((slot) => {
    const next = removeWidgetById(slot, id);
    if (next !== slot) changed = true;
    return next;
  });
  if (!changed) return root;
  return { ...root, widget: { ...root.widget, slots } };
}

/**
 * Resize a layout widget to `rows` × `columns`, preserving its content:
 * slots keep their (row, column) position when it still exists, and widgets
 * whose cell disappears are relocated into the remaining empty cells in
 * row-major order. Returns `null` when the resize would lose a widget (more
 * occupied slots than the new grid has cells) or when the dimensions are
 * not positive integers — callers should disable the action rather than
 * destroy content. Track sizes are truncated or padded with `1` (an equal
 * share) to the new counts. Returns the layout unchanged when the
 * dimensions already match.
 */
export function resizeCanvasLayout(
  layout: CanvasLayoutWidget,
  rows: number,
  columns: number
): CanvasLayoutWidget | null {
  if (!isSlotCount(rows) || !isSlotCount(columns)) return null;
  if (rows === layout.rows && columns === layout.columns) return layout;

  const slots: CanvasSlot[] = Array.from(
    { length: rows * columns },
    (): CanvasSlot => null
  );
  const displaced: CanvasWidget[] = [];
  for (let row = 0; row < layout.rows; row += 1) {
    for (let column = 0; column < layout.columns; column += 1) {
      const slot = layout.slots[row * layout.columns + column] ?? null;
      if (!slot) continue;
      if (row < rows && column < columns) slots[row * columns + column] = slot;
      else displaced.push(slot);
    }
  }
  let cursor = 0;
  for (const widget of displaced) {
    while (cursor < slots.length && slots[cursor] !== null) cursor += 1;
    if (cursor >= slots.length) return null;
    slots[cursor] = widget;
  }

  const resizeTrack = (
    sizes: number[] | undefined,
    count: number
  ): number[] | undefined => {
    if (!sizes) return undefined;
    return Array.from({ length: count }, (_, index) => sizes[index] ?? 1);
  };
  const rowSizes = resizeTrack(layout.rowSizes, rows);
  const columnSizes = resizeTrack(layout.columnSizes, columns);
  return {
    kind: "layout",
    rows,
    columns,
    slots,
    ...(rowSizes ? { rowSizes } : {}),
    ...(columnSizes ? { columnSizes } : {}),
  };
}

/**
 * Remove row or column `index` from a layout widget, preserving content:
 * widgets living in the removed track relocate into the remaining empty
 * cells in row-major order. Returns `null` when the removal would lose a
 * widget (not enough free cells), when the axis has only one track, or when
 * `index` is out of range — callers should disable the action. Track sizes
 * drop the removed entry (the remaining `fr` shares renormalize by
 * themselves).
 */
export function removeCanvasLayoutTrack(
  layout: CanvasLayoutWidget,
  axis: "rows" | "columns",
  index: number
): CanvasLayoutWidget | null {
  const count = axis === "rows" ? layout.rows : layout.columns;
  if (!Number.isInteger(index) || index < 0 || index >= count) return null;
  if (count <= 1) return null;
  const rows = axis === "rows" ? layout.rows - 1 : layout.rows;
  const columns = axis === "columns" ? layout.columns - 1 : layout.columns;

  const slots: CanvasSlot[] = [];
  const displaced: CanvasWidget[] = [];
  for (let row = 0; row < layout.rows; row += 1) {
    for (let column = 0; column < layout.columns; column += 1) {
      const slot = layout.slots[row * layout.columns + column] ?? null;
      const removed = axis === "rows" ? row === index : column === index;
      if (!removed) slots.push(slot);
      else if (slot) displaced.push(slot);
    }
  }
  let cursor = 0;
  for (const widget of displaced) {
    while (cursor < slots.length && slots[cursor] !== null) cursor += 1;
    if (cursor >= slots.length) return null;
    slots[cursor] = widget;
  }

  const dropTrack = (sizes: number[] | undefined): number[] | undefined =>
    sizes?.filter((_, position) => position !== index);
  const rowSizes =
    axis === "rows" ? dropTrack(layout.rowSizes) : layout.rowSizes;
  const columnSizes =
    axis === "columns" ? dropTrack(layout.columnSizes) : layout.columnSizes;
  return {
    kind: "layout",
    rows,
    columns,
    slots,
    ...(rowSizes ? { rowSizes } : {}),
    ...(columnSizes ? { columnSizes } : {}),
  };
}

/** Smallest share of its axis a track can be dragged down to (5%). */
const MIN_TRACK_SHARE = 0.05;

const roundTrackSize = (size: number): number => Math.round(size * 1000) / 1000;

/**
 * Trade axis share between the adjacent tracks `index` and `index + 1`:
 * `delta` (in normalized track units, positive grows the first) moves their
 * common edge while their sum — and every other track — stays put, so the
 * pair exchanges percentage of the axis. Sizes are normalized so they sum
 * to the track count (an equal split is all `1`s, i.e. 50/50 for two
 * tracks) and each track keeps at least {@link MIN_TRACK_SHARE} of the
 * axis. Rounded to 3 decimals; an out-of-range index yields the normalized
 * sizes unchanged.
 */
export function resizeTrackPair(
  sizes: readonly number[],
  index: number,
  delta: number
): number[] {
  const count = sizes.length;
  const total = sizes.reduce((sum, size) => sum + size, 0);
  if (!(total > 0)) return Array.from({ length: count }, () => 1);
  const normalized = sizes.map((size) => (size / total) * count);
  const first = normalized[index];
  const second = normalized[index + 1];
  if (first === undefined || second === undefined) {
    return normalized.map(roundTrackSize);
  }
  const minimum = MIN_TRACK_SHARE * count;
  const pair = first + second;
  if (pair > minimum * 2) {
    const moved = Math.min(Math.max(first + delta, minimum), pair - minimum);
    normalized[index] = moved;
    normalized[index + 1] = pair - moved;
  }
  return normalized.map(roundTrackSize);
}

/** The widget with the given id, or `undefined`. */
export function findWidgetById(
  root: CanvasSlot,
  id: string
): CanvasWidget | undefined {
  if (!root) return undefined;
  if (root.id === id) return root;
  if (root.widget.kind !== "layout") return undefined;
  for (const slot of root.widget.slots) {
    const found = findWidgetById(slot, id);
    if (found) return found;
  }
  return undefined;
}
